import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatNumber } from "../utils/formatNumber";

export default function ImageModal({ open, onClose, imageDetail, type }) {
  const images = useMemo(() => {
    return imageDetail?.images || [];
  }, [imageDetail]);
  
  const [current, setCurrent] = useState(images[0] || null);

  useEffect(() => {
    setCurrent(images[0] || null);
  }, [imageDetail, images]);

  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // reset ทุกครั้งที่เปลี่ยนรูป
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, [current]);

  const handleWheel = (e) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const zoomIntensity = 1;
    const newScale =
      e.deltaY < 0
        ? Math.min(scale + zoomIntensity, 5) // max 5x
        : Math.max(scale - zoomIntensity, 1); // min 1x

    if (newScale === 1) {
      setOffset({ x: 0, y: 0 });
    } else {
      const scaleFactor = newScale / scale;

      setOffset({
        x: mouseX - (mouseX - offset.x) * scaleFactor,
        y: mouseY - (mouseY - offset.y) * scaleFactor,
      });
    }

    setScale(newScale);
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const datePart = d.toLocaleDateString("th-TH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString("th-TH", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${datePart} เวลา ${timePart}`;
  };

  const isSelected = (img) => current && img?.id === current?.id;

  const weight = imageDetail?.weight || 0;
  const deduct = imageDetail?.deductWeight || 0;
  const net = imageDetail?.netWeight ?? weight - deduct;
  const impurityWeight =
    imageDetail.impurities?.reduce((sum, i) => sum + (i.weight ?? 0), 0) ?? 0;
  const deductedWeight =
    imageDetail.deductedItems?.reduce((sum, di) => sum + (di.weight ?? 0), 0) ??
    0;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography sx={{ fontSize: "24px", fontWeight: 700 }}>
            รูปภาพการชั่งน้ำหนัก
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {type === "car" && (
              <>
                <Typography>
                  รถหนัก: {formatNumber(imageDetail?.carHeavyWeight)} รถเบา:{" "}
                  {formatNumber(imageDetail?.carLightWeight)} ={" "}
                  {formatNumber(
                    imageDetail?.carHeavyWeight - imageDetail?.carLightWeight
                  )}
                </Typography>
                <IconButton onClick={onClose} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
            {type !== "car" && (
              <>
                <Typography sx={{ fontWeight: 700 }}>
                  {imageDetail?.productName}
                </Typography>
                <Typography>
                  รวม {formatNumber(weight)} หัก {formatNumber(deduct)}{" "}
                  สิ่งเจือปน: {formatNumber(impurityWeight + deductedWeight)} ={" "}
                  {formatNumber(net)}
                </Typography>
                <IconButton onClick={onClose} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
          {/* Main image */}
          <Box
            ref={containerRef}
            onWheel={handleWheel}
            sx={{
              flex: 1,
              p: 1.5,
              backgroundColor: "#fafafa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Paper
              elevation={3}
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "#000",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {current ? (
                <>
                  <img
                    src={current.imageUrl}
                    alt="current"
                    style={{
                      transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                      transformOrigin: "0 0",
                      transition: "transform 0.1s ease-out",
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      px: 1,
                      py: 1,
                      fontSize: "24px",
                      textAlign: "right",
                    }}
                  >
                    {formatDate(current.createdAt)}
                  </Box>
                </>
              ) : (
                <Typography color="text.secondary">ไม่มีรูปภาพ</Typography>
              )}
            </Paper>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Thumbnails */}
          <Box sx={{ width: 200, p: 1, bgcolor: "#f5f5f5", overflowY: "auto" }}>
            {images.map((thumb) => (
              <Box
                key={thumb.id}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  overflow: "hidden",
                  cursor: "pointer",
                  border: isSelected(thumb)
                    ? "2px solid #1976d2"
                    : "1px solid #ddd",
                  position: "relative",
                }}
                onClick={() => setCurrent(thumb)}
              >
                <img
                  src={thumb.imageUrl}
                  alt={`thumb-${thumb.id}`}
                  style={{
                    width: "100%",
                    height: 120,
                    objectFit: "cover",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    px: 1,
                    py: 1,
                    fontSize: "14px",
                    textAlign: "right",
                  }}
                >
                  {formatDate(thumb.createdAt)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
